/**
 * TrainingDashboardPage — `/franchise/training`.
 *
 * Backed by the dedicated `/v1/franchise/training` surface (module-level
 * records). Combines the franchise lifecycle queue (APPROVED / TRAINING)
 * with per-module progress + scores.
 */
import { type JSX, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  type FranchiseTrainingRecord,
  listFranchises,
  listTrainingRecords,
  type TrainingStatus,
  upsertTrainingRecord,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const STATUSES: readonly TrainingStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'FAILED'];

export function TrainingDashboardPage(): JSX.Element {
  const qc = useQueryClient();
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<TrainingStatus | ''>('');

  const [recordOpen, setRecordOpen] = useState(false);
  const [recFranchiseId, setRecFranchiseId] = useState('');
  const [recModuleCode, setRecModuleCode] = useState('NIPOST-OPS-101');
  const [recModuleName, setRecModuleName] = useState('NIPOST Operations 101');
  const [recStatus, setRecStatus] = useState<TrainingStatus>('IN_PROGRESS');
  const [recProgress, setRecProgress] = useState('50');
  const [recScore, setRecScore] = useState('');

  const records = useQuery({
    queryKey: ['franchise', 'training', 'records', statusFilter],
    queryFn: () =>
      listTrainingRecords(statusFilter === '' ? {} : { status: statusFilter }),
    retry: false,
  });

  const franchises = useQuery({
    queryKey: ['franchise', 'training', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const denied = useMemo(
    () =>
      [records, franchises].some(
        (q) =>
          q.isError && q.error instanceof ApiError &&
          (q.error.status === 401 || q.error.status === 403),
      ),
    [records, franchises],
  );

  const franchiseIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of franchises.data?.items ?? []) {
      map.set(f.id, `${f.legalName} (${f.franchiseNumber})`);
    }
    return map;
  }, [franchises.data]);

  const upsert = useMutation({
    mutationFn: () =>
      upsertTrainingRecord({
        franchiseId: recFranchiseId,
        moduleCode: recModuleCode,
        moduleName: recModuleName,
        status: recStatus,
        progressPercent: Number.parseInt(recProgress || '0', 10),
        score: recScore.length === 0 ? undefined : Number.parseInt(recScore, 10),
      }),
    onSuccess: () => {
      setRecordOpen(false);
      void qc.invalidateQueries({ queryKey: ['franchise', 'training'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const items: readonly FranchiseTrainingRecord[] = records.data ?? [];
  const inProgress = items.filter((r) => r.status === 'IN_PROGRESS').length;
  const completed = items.filter((r) => r.status === 'COMPLETED').length;
  const failed = items.filter((r) => r.status === 'FAILED').length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Training dashboard"
        description="Module-level training records — progress, scores, instructor sign-off."
        actions={
          <button
            type="button"
            onClick={() => setRecordOpen((v) => !v)}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            {recordOpen ? 'Close' : 'Record progress'}
          </button>
        }
      />

      {recordOpen ? (
        <section
          data-testid="training-record-form"
          className="bg-white border border-border rounded-xl p-5 space-y-3"
        >
          <h2 className="text-sm font-semibold text-gray-900">Record / update training progress</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
                Franchise
              </label>
              <select
                value={recFranchiseId}
                onChange={(e) => setRecFranchiseId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                <option value="">Select…</option>
                {franchises.data?.items.map((f) => (
                  <option key={f.id} value={f.id}>{f.legalName} ({f.franchiseNumber})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
                Module code
              </label>
              <input
                type="text"
                value={recModuleCode}
                onChange={(e) => setRecModuleCode(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
                Module name
              </label>
              <input
                type="text"
                value={recModuleName}
                onChange={(e) => setRecModuleName(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
                Status
              </label>
              <select
                value={recStatus}
                onChange={(e) => setRecStatus(e.target.value as TrainingStatus)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
                Progress %
              </label>
              <input
                type="number"
                min={0}
                max={100}
                value={recProgress}
                onChange={(e) => setRecProgress(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
                Score (optional)
              </label>
              <input
                type="number"
                min={0}
                max={100}
                value={recScore}
                onChange={(e) => setRecScore(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setActionErr(null);
              upsert.mutate();
            }}
            disabled={recFranchiseId.length === 0 || upsert.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            data-testid="training-submit-btn"
          >
            {upsert.isPending ? 'Saving…' : 'Save progress'}
          </button>
        </section>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard title="In progress" value={String(inProgress)} />
        <KPICard title="Completed" value={String(completed)} />
        <KPICard title="Failed" value={String(failed)} />
      </div>

      <div className="bg-white border border-border rounded-xl p-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as TrainingStatus | '')}
          className="px-3 py-2 border border-border rounded-lg text-sm"
        >
          <option value="">All training records</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseTraining/read</code> required.
        </DeferredNotice>
      ) : records.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No training records match the filter.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Franchise</th>
                <th className="text-left px-4 py-2">Module</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Progress</th>
                <th className="text-left px-4 py-2">Score</th>
                <th className="text-left px-4 py-2">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50" data-testid="training-row">
                  <td className="px-4 py-2 text-xs text-gray-700">
                    {franchiseIndex.get(r.franchiseId) ?? r.franchiseId}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <span className="block font-medium text-gray-900">{r.moduleName}</span>
                    <span className="font-mono text-[11px] text-muted">{r.moduleCode}</span>
                  </td>
                  <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-2 text-xs text-gray-700">{r.progressPercent}%</td>
                  <td className="px-4 py-2 text-xs text-gray-700">{r.score ?? '—'}</td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(r.updatedAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}
    </div>
  );
}
